import { NumberFormatStyle, getNumberOfCurrencyDigits } from '@angular/common';
import {
  Component,
  Inject,
  Input,
  LOCALE_ID,
  Optional,
  Self,
  ɵCurrencyIndex,
  ɵLocaleDataIndex,
  ɵfindLocaleData,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { getCurrentRegisterCurrency } from '@assetaccountant/core/pipes/aa-currency.pipe';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { isNumeric, roundTo } from '@assetaccountant/core/utils/numeric.utils';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';

export interface CurrencyInfo {
  code: string;
  symbol: string;
  symbolPosition: 'START' | 'END' | undefined;
  nbOfDigits: number;
}

export const getCurrencyInfoFromRoute = (
  appLocale: string,
  route: TypedActivatedRoute<RegisterParams, RegisterData>,
): CurrencyInfo => {
  const code = getCurrentRegisterCurrency(route.snapshot);

  return getCurrencyInfo(appLocale, code);
};

export const getCurrencyInfo = (appLocale: string, code: string): CurrencyInfo => {
  const localeData = ɵfindLocaleData(appLocale) as {
    [ɵLocaleDataIndex.CurrencyCode]: string;
    [ɵLocaleDataIndex.CurrencySymbol]: string;
    [ɵLocaleDataIndex.Currencies]: Record<string, [string] | [undefined, string] | undefined>;
    [ɵLocaleDataIndex.NumberFormats]: Record<NumberFormatStyle, string>;
  };

  // Symbol, narrow symbol or code
  const symbol =
    localeData[ɵLocaleDataIndex.CurrencyCode] === code
      ? localeData[ɵLocaleDataIndex.CurrencySymbol]
      : // Prefer narrow symbol
        localeData[ɵLocaleDataIndex.Currencies][code]?.[ɵCurrencyIndex.SymbolNarrow] ||
        localeData[ɵLocaleDataIndex.Currencies][code]?.[ɵCurrencyIndex.Symbol] ||
        code;

  const symbolIsRtl = /[\u0591-\u07FF\u200F\uFB1D-\uFDFD\uFE70-\uFEFC]$/.test(symbol);

  // Is the currency placeholder at the end of the format?
  // Or is it at the start (without a space) and the symbol is right-to-left
  const symbolPosition =
    /¤$/.test(localeData[ɵLocaleDataIndex.NumberFormats][NumberFormatStyle.Currency]) ||
    (/^¤#/.test(localeData[ɵLocaleDataIndex.NumberFormats][NumberFormatStyle.Currency]) &&
      symbolIsRtl)
      ? 'END'
      : 'START';

  const nbOfDigits = code === 'IDR' ? 0 : getNumberOfCurrencyDigits(code);

  return { code, symbol, symbolPosition, nbOfDigits };
};

@Component({
  selector: 'app-currency-input',
  templateUrl: './currency-input.component.html',
  standalone: false,
})
export class CurrencyInputComponent implements ControlValueAccessor {
  model: number | null = null;

  symbol = '';
  symbolPosition?: 'START' | 'END';
  nbOfDigits = 0;

  @Input() maxDecimalPlaces: number | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;

  disabled = false;

  private _onChange?: (val: number | null) => void;
  private _onTouched?: () => void;

  get showError() {
    return !!(this.control && this.control.invalid && this.control.touched);
  }

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    @Self() @Optional() private control?: NgControl,
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
    const currency = getCurrencyInfoFromRoute(this.locale, this.route);

    this.symbol = currency.symbol;
    this.symbolPosition = currency.symbolPosition;
    this.nbOfDigits = currency.nbOfDigits;
  }

  writeValue(value: number | null): void {
    const rounded = isNumeric(value) ? this.getRoundedAmount(+value) : null;
    this.model = rounded;
    if (rounded !== value) {
      this._onChange?.(rounded);
    }
  }

  onChange(val: string | number | null) {
    if (isNumeric(val)) {
      this._onChange?.(this.getRoundedAmount(+val));
    } else {
      this._onChange?.(null);
    }
  }

  onTouched() {
    if (isNumeric(this.model)) {
      const roundedModel = this.getRoundedAmount(this.model);
      if (this.model !== roundedModel) {
        this.model = this.getRoundedAmount(this.model);
        this.onChange?.(this.model);
      }
    }
    this._onTouched?.();
  }

  private getRoundedAmount = (amount: number) =>
    roundTo(amount, this.maxDecimalPlaces ?? this.nbOfDigits);

  registerOnChange(fn: (val: number | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
